<template>
  <div
    class="main-product component-section critical-component-clear"
    :class="{ [$class('loading')]: !getComponentActive('main-product') || state === 'loading' }"
  >
    <div class="main-product__container grid">
      <div class="col xs4 l6">
        <div class="main-product__gallery">
          <product-gallery
            v-if="product"
            :images="productImages"
            :videos="productVideos"
            :section-id="liquid.sectionId"
            :gallery-image-ratio="liquid.galleryImageRatio"
            :state="state"
            :selected-image-id="selectedImageId"
          />
        </div>
      </div>

      <div class="col xs4 l6 main-product__form-wrapper">
        <div
          v-if="hasReviewStars"
          class="main-product__star-reviews yotpo bottomLine"
          :data-appkey="liquid.yotpoApiKey"
          :data-domain="permanentDomain"
          :data-product-id="product.id"
          :data-product-models="product.id"
          :data-name="product.title"
          :data-url="productUrl"
          :data-image-url="product.featured_image"
          :data-description="product.description"
          :data-bread-crumbs="productTags"
        />

        <h1
          :id="`section-title-${liquid.sectionId}`"
          class="main-product__title"
          v-text="product.title || liquid.title"
        />

        <product-usp
          v-if="sellingPoints || liquid.uspList"
          :list="sellingPoints || liquid.uspList"
        />

        <div
          v-if="showPromoCallMessage"
          class="main-product__promo-item text-body-s"
        >
          <Bottle />
          {{ promoItem }}
        </div>

        <div class="main-product__form">
          <product-form
            :liquid="liquid"
            :product="product"
            :variant="variant"
            :recharge="recharge"
            :swatch-products="swatchProducts"
            :can-have-promo-item="canHavePromoItem"
            :promo-item="liquid.promoItem"
            :enable-gift-messaging="liquid.enableGiftMessaging"
            :is-gift-card-template="liquid.isGiftCardTemplate"
            @update-variant="handleVariantUpdate"
            @update-linked-variant="handleLinkedVariantUpdate"
            @update-promo-item="handlePromoItem"
          />
        </div>

        <div
          class="main-product__description rte text-body-s text-body-m-desktop"
          v-html="product.description || liquid.description"
        />

        <div class="main-product__accordion">
          <accordion-item
            v-for="(usp, index) in usp.blocks"
            :key="`${usp.title}-${index}`"
            :index="index"
            :content="usp.content"
            :toggle-index="toggleIndex"
            :multi-open="false"
            :block-class="'main-product'"
            @accordion-toggle="handleAccordionToggle"
          >
            <template #title>
              {{ usp.title }}
            </template>
            <template #content>
              <div
                v-if="usp.content"
                class="main-product__accordion-content-inner"
                v-html="usp.content"
              />
            </template>
          </accordion-item>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} handle - Product page's original handle.
 * @param {String} liquid.description - Product's Liquid description.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {String} liquid.price - Product's Liquid price.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Product's Liquid title.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import { mapState } from 'vuex'
import AccordionItem from '~async/accordion/accordion-item'
import ProductGallery from '~async/main-product/product-gallery'
import ProductForm from '~async/product-form/product-form'
import ProductUsp from '~async/main-product/product-usp'
import Cookies from 'js-cookie'
import productByHandleWithMetafields from '~/graphql/queries/productByHandleWithMetafields.gql'
import productByIdWithMetafields from '~/graphql/queries/productByIdWithMetafields.gql'
import { formatProduct, formatDateFromDays} from '~/helpers/format'
import { nextTick } from 'vue'
import Bottle from '~icons/misc/bottle.svg'
import classes from '~/config/classes'

export default {
  name: 'MainProduct',

  components: {
    AccordionItem,
    ProductForm,
    ProductGallery,
    ProductUsp,
    Bottle,
  },

  props: {
    handle: {
      type: String,
      default: cnvs.page.handle,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        hasOnlyDefaultVariant: false,
        price: 0,
        sectionId: '',
        title: '',
        galleryImageRatio: {},
        uspList: [],
        enableYotpo: false,
        isGiftCardTemplate: false,
      }),
    },
    recharge: {
      type: Object,
      default: () => ({
      }),
    },
    product: {
      type: [Object, Boolean],
      default: false,
    },
    variant: {
      type: [Object, Boolean],
      default: false,
    },
    usp: {
      type: Object,
      default: () => ({
        blocks: [],
      }),
    },
    hasSwatches: {
      type: Boolean,
      default: true,
    },
    wineFamily: {
      type: String,
      default: null,
    },
    enableGiftMessaging: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update-request-object'],

  data() {
    return {
      currentHandle: cnvs.page.handle,
      daysToExpire: 30,
      toggleIndex: {},
      productImages: [],
      productVideos: [],
      swatchProducts: [],
      sellingPoints: [],
      swatches: [],
      forceUpdate: false,
      state: 'loading',
      selectedImageId: null,
      canHavePromoItem: false,
      hasReviewStars: false,
      yotpoScriptEnabled: document.querySelector('script[src^="//staticw2.yotpo.com"]') ? true : false,
      yotpoWidget: document.querySelector('#vue-element .yotpo.yotpo-main-widget') || null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      screen: (state) => state.index.screen,
      settings: (state) => state.index.settings,
    }),

    permanentDomain() {
      return window.cnvs.store.permanentDomain
    },

    productUrl() {
      return `${window.cnvs.request.origin}${window.cnvs.request.path}`
    },

    productTags() {
      if (!this.product?.tags) {
        return
      }

      return `${this.product.tags.join(';')};`
    },

    promoItem() {
      return this.$string('product.form.promo_item')
    },

    isYotpoHidden() {
      if (this.product.disableReviews) {
        if (this.product.disableReviews.value === 'true') {
          return true
        }
      }

      return false
    },

    showPromoCallMessage() {
      if (!this.canHavePromoItem) {
        return false
      }

      return (this.canHavePromoItem && !this.checkPromoItemInCart()) ? true : false
    }
  },

  watch: {

    /**
     * Watch currentHandle for update to load new product.
     */
    currentHandle() {
      this.queryProduct(this.currentHandle)
    },

    state() {
      if (this.state === 'ready') {
        setTimeout(() => {
          this.initYotpo()
        },1000)
      }
    },


    product() {
      this.initRiseGiftCard()

      if (this.product.sellingPoints?.value) {
        this.sellingPoints = this.formatSellingPoints(this.product.sellingPoints.value)
      }

      if (
        !this.hasSwatches ||
        this.swatchProducts.length
      ) {
        return
      }

      if (!this.liquid.productFamily) {
        return
      }

      for (let i = 0; i < this.liquid.productFamily.length; i++) {
        this.querySwatchProducts(this.liquid.productFamily[i], i)
      }

      if (this.product.tags.includes('has-promo-item') && Cookies.get('promo_item')) {
        if (Cookies.get('collection')) {
          const inCollection = this.liquid.collections.some(el => el.handle === Cookies.get('collection'))

          if (!inCollection) {
            return
          }
        }

        this.canHavePromoItem = true
      } else {
        return
      }
    },

    variant() {
      this.productImages = this.filterGalleryImages(this.product.images, this.variant.title)
      this.productVideos = this.product.videos
      this.selectedImageId = this.productImages[0].id
    },
  },

  mounted() {
    if (!Cookies.get('promo_item')) {
      this.checkPromoItem()
    }
    this.queryProduct()
    this.setEventBusListeners()
  },

  methods: {

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }

      if (this.product.handle === handle) {
        this.state = 'ready'
        return
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productByHandleWithMetafields,
          variables: {
            country: cnvs.store.country,
            handle,
            namespace: 'product',
            familyKey: 'product_family',
            linkedKey: 'linked_option_name',
            sellingPoints: 'selling_points',
            variantOptionDesc: 'option_descriptions',
            disableReviews: 'disable_reviews',
            subscriptionExpiryKey: 'subscription_expiry',
            giftProductKey: 'gift_product',
            subscriptionNameSpace: 'subscriptions',
            hasSubscriptionKey: 'has_subscription',
            subscriptionOnlyKey: 'is_subscription_only',
            shippingIntervalFrequencyKey: 'shipping_interval_frequency',
            shippingIntervalUnitTypeKey: 'shipping_interval_unit_type',
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response),
          type: 'product',
        })

        // emit product to section settings listening for product updates
        cnvs.EventBus.emit('update-request-object', formatProduct(response))
        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [id] - Product id to load.
     * @param {Number} [index] - what order it belongs to.
     */
    async querySwatchProducts(id, index) {
      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productByIdWithMetafields,
          variables: {
            country: cnvs.store.country,
            id,
            namespace: 'product',
            familyKey: 'product_family',
            linkedKey: 'linked_option_name',
            sellingPoints: 'selling_points',
            variantOptionDesc: 'option_descriptions',
            disableReviews: 'disable_reviews',
            giftProductKey: 'gift_product',
            subscriptionExpiryKey: 'subscription_expiry',
            subscriptionNameSpace: 'subscriptions',
            hasSubscriptionKey: 'has_subscription',
            subscriptionOnlyKey: 'is_subscription_only',
            shippingIntervalFrequencyKey: 'shipping_interval_frequency',
            shippingIntervalUnitTypeKey: 'shipping_interval_unit_type',
          },
        })

        if(!response) {
          return
        }

        this.swatchProducts[index] = response.product
      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handles product gallery images to only show current variant global product images
     */
    filterGalleryImages(images, variant) {
      return images.filter(image => image.alt === variant.replace(' ', '-').toLowerCase() || image.alt === null)
    },

    formatSellingPoints(points) {
      return JSON.parse(points)
    },

    /**
     * Handles accordion item click
     * @param {Number} index - index of accordion that was clicked
     * - force updates the toggle index
     */
    handleAccordionToggle(index) {
      this.toggleIndex = {
        index,
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const url = variant?.id
        ? `${location.origin}${location.pathname}?variant=${variant.id}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleLinkedVariantUpdate(variant) {
      const formattedVariant = formatProduct(variant)

      this.$emit('update-request-object', {
        response: formattedVariant,
        type: 'product',
      })

      // emit product to section settings listening for product updates
      cnvs.EventBus.emit('update-request-object', formattedVariant)

      this.$emit('update-request-object', {
        response: formattedVariant.variants[0],
        type: 'variant',
      })

      const url = formattedVariant?.id
        ? `${location.origin}/products/${formattedVariant.handle}?variant=${formattedVariant.variants[0].id}`
        : `${location.origin}/products/${formattedVariant.handle}`

      history.replaceState({
        html: '',
      }, '', url)

      this.refreshYotpoAttr()
    },

    /**
     * Initialize Rise script for digitial gift card when switching between linked products
     * - script loads only once
     */
    initRiseGiftCard() {
      if (document.querySelector('script[src^="https://str.rise-ai.com"]') || !this.product.giftCard) {
        return
      }

      const baseUrl = 'https://str.rise-ai.com'
      const shopDomain = window.cnvs.store.permanentDomain.replaceAll('/', '')
      const script = document.createElement('script')
      script.setAttribute('src', `${baseUrl}/?shop=${shopDomain}`)
      script.defer = true

      document.head.appendChild(script)
    },

    /**
     * Refresh Yotpo
     */
    refreshYotpo() {
      this.yotpoWidget = document.querySelector('#vue-element .yotpo.yotpo-main-widget')

      if (!this.yotpoScriptEnabled && !this.yotpoWidget) {
        return
      }

      if (this.isYotpoHidden) {
        this.hasReviewStars = false

        if (this.yotpoWidget) {
          this.yotpoWidget.classList.remove(classes.active)
        }

        return
      }

      this.hasReviewStars = true

      nextTick(() => {
        if (typeof Yotpo === 'function') {
          if (!this.yotpoApi) {
            this.yotpoApi = new Yotpo.API(yotpo)
          }
          this.yotpoApi.refreshWidgets()

          if(this.yotpoWidget) {
            this.updateYotpoWidget()
          }

          return
        }

        setTimeout(() => {
          this.refreshYotpo()
        }, this.settings.throttle)
      })
    },

    /**
     * Update Yotpo widget elements in the DOM
     */
    updateYotpoWidget() {
      this.yotpoWidget.classList.add(classes.active)

      setTimeout(() => {
        const reviewSummaryContainer = this.yotpoWidget.querySelector('.yotpo-stars-and-sum-reviews')
        if (!reviewSummaryContainer) {
          return
        }

        const widgetSummaryReviewStars = reviewSummaryContainer.querySelector('.yotpo-stars .sr-only')

        if (widgetSummaryReviewStars) {
          const ratingCount = widgetSummaryReviewStars.innerText.replace('star rating', '').trim()
          widgetSummaryReviewStars.innerHTML = ratingCount
        }

        const widgetTitle = `
          <h2 class="yotpo-main-widget__title text-heading-l text-heading-2xl-desktop">
            ${this.$string('product.reviews_widget.title') || 'Customer Reviews'}
          </h2>
        `

        reviewSummaryContainer.insertAdjacentHTML('beforeBegin', widgetTitle)
      }, 500)
    },

    /**
     * Handles Yotpo initialization
     */
    initYotpo() {
      this.yotpoWidget = document.querySelector('#vue-element .yotpo.yotpo-main-widget')

      if (!this.yotpoScriptEnabled && this.yotpoWidget) {
        this.yotpoWidget.remove()
        return
      }

      if (this.isYotpoHidden) {
        this.hasReviewStars = false

      } else {
        this.hasReviewStars = true
        this.refreshYotpo()
      }
    },

    /**
     * Updates Yotpo widget data attributes and triggers a refresh
     */
    refreshYotpoAttr() {
      if (!this.yotpoScriptEnabled && !this.yotpoWidget) {
        return
      }

      nextTick(() => {
        if (this.yotpoWidget) {
          this.yotpoWidget.dataset.productId = this.product.id
          this.yotpoWidget.dataset.name = this.product.title
          this.yotpoWidget.dataset.url = `https:${this.product.url}`
          this.yotpoWidget.dataset.imageUrl = this.product.featuredImage.src
          this.yotpoWidget.dataset.description = this.product.description
        }

        this.refreshYotpo()
      })
    },

    checkPromoItem() {
      const query = window.location.search.replace('?', '')
      const vars = query.split('&')
      const monthLater = formatDateFromDays(this.daysToExpire)

      if (query.length === 0) {
        return false
      }

      for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=')

        if (pair[0] === 'pb') {
          Cookies.set('promo_item', monthLater, { expires: this.daysToExpire })
          Cookies.remove('promo_item_added')
        }

        if (pair[0] === 'collection') {
          Cookies.set('collection', pair[1], { expires: this.daysToExpire })
        }
      }

      if (!this.checkPromoItemInCart()) {
        Cookies.remove('promo_item_added')
      }
    },

    checkPromoItemInCart() {
      const hasPromoItemInCart = this.cart.items.find((item) => {
        return item.tags.includes('has-promo-item')
      })

      return hasPromoItemInCart ? true : false
    },

    handlePromoItem() {
      const promoItemCookie = Cookies.get('promo_item')

      if (promoItemCookie) {
        // Backwards compatibility check with legacy promo cookie format
        if (promoItemCookie === 'true') {
          Cookies.set('promo_item_added', this.daysToExpire, { expires: this.daysToExpire })
        } else {
          // Calculates days left from last in cookie expirations for 'promo_item_added'
          const cookieExpireTime = new Date(promoItemCookie)
          const now = new Date()
          const diff = cookieExpireTime - now
          const daysLeft = Math.round( (diff) / (1000 * 60 * 60 * 24) )
          Cookies.set('promo_item_added', daysLeft, { expires: daysLeft })
        }
      }

      Cookies.remove('promo_item')
      Cookies.remove('collection')
    },

    /**
     * Event listener to update promo item cookie when promo removed
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('lineItemUpdate:removePromoItem', () => {
        if (Cookies.get('promo_item_added')) {
          const daysLeftToExpire = parseInt(Cookies.get('promo_item_added')) || this.daysToExpire
          const dateLeft = formatDateFromDays(daysLeftToExpire)
          Cookies.set('promo_item', dateLeft, { expires: daysLeftToExpire })
        } else {
          const monthLater = formatDateFromDays(this.daysToExpire)
          Cookies.set('promo_item', monthLater, { expires: this.daysToExpire })
        }

        Cookies.remove('promo_item_added')

        if (this.product.tags.includes('has-promo-item')) {
          this.canHavePromoItem = true
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
@import './main-product-reviews-widget';
</style>
